/**
 * Компонент Toast
 * Уведомления вместо alert() в стиле 21st.dev
 * Автор: AI Market Hub
 */

import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect } from 'react';
import { createRoot } from 'react-dom/client';

// Глобальное состояние для всех toast
let toastContainer = null;
let toastId = 0;

// Иконки для разных типов
const icons = {
  success: '✅',
  error: '❌',
  warning: '⚠️',
  info: 'ℹ️',
};

// Цвета для разных типов
const colors = {
  success: 'from-green-500 to-emerald-500 border-green-500/30',
  error: 'from-red-500 to-rose-500 border-red-500/30',
  warning: 'from-yellow-500 to-orange-500 border-yellow-500/30',
  info: 'from-blue-500 to-cyan-500 border-blue-500/30',
};

// Компонент одного Toast
function ToastItem({ id, message, type = 'info', duration = 3000, onClose }) {
  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        onClose(id);
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [id, duration, onClose]);

  return (
    <motion.div
      initial={{ opacity: 0, x: 300, scale: 0.9 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 300, scale: 0.9 }}
      transition={{ type: 'spring', damping: 20, stiffness: 300 }}
      className={`
        relative mb-3 p-4 rounded-xl
        bg-gradient-to-r ${colors[type]}
        border border-white/20
        shadow-lg backdrop-blur-sm
        min-w-[300px] max-w-[500px]
      `}
    >
      <div className="flex items-start gap-3">
        <span className="text-2xl flex-shrink-0">{icons[type]}</span>
        <p className="text-white font-medium flex-1 text-sm leading-relaxed">
          {message}
        </p>
        <button
          onClick={() => onClose(id)}
          className="flex-shrink-0 text-white/80 hover:text-white transition-colors"
        >
          ✕
        </button>
      </div>
    </motion.div>
  );
}

// Контейнер для всех Toast
function ToastContainer() {
  const [toasts, setToasts] = useState([]);

  // Глобальная функция для добавления toast
  useEffect(() => {
    window.showToast = (message, type = 'info', duration = 3000) => {
      const id = toastId++;
      setToasts(prev => [...prev, { id, message, type, duration }]);
    };
  }, []);

  const handleClose = (id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  return (
    <div className="fixed top-20 right-4 z-[9999] pointer-events-none">
      <AnimatePresence>
        {toasts.map(toast => (
          <div key={toast.id} className="pointer-events-auto">
            <ToastItem {...toast} onClose={handleClose} />
          </div>
        ))}
      </AnimatePresence>
    </div>
  );
}

// Инициализация контейнера
export function initToast() {
  if (toastContainer) return;
  
  const container = document.createElement('div');
  container.id = 'toast-container';
  document.body.appendChild(container);
  
  const root = createRoot(container);
  root.render(<ToastContainer />);
  toastContainer = container;
}

// Функция для показа toast (совместимость с alert)
export function showToast(message, type = 'info', duration = 3000) {
  if (!toastContainer) {
    initToast();
    // Ждем немного для инициализации
    setTimeout(() => {
      if (window.showToast) {
        window.showToast(message, type, duration);
      }
    }, 100);
  } else {
    if (window.showToast) {
      window.showToast(message, type, duration);
    }
  }
}

// Экспорт по умолчанию
export default { showToast, initToast };




